
import { ButtonGroup, FormControl, FormLabel, Input, Select, Switch } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import ButtonBack from 'components/ui/ButtonBack';
import CardForm from 'components/ui/CardForm';
import CustomSingleDatepicker from 'components/ui/Datepicker/CustomSingleDatepicker';
import FixedBar from 'components/ui/FixedBar';
import FormControlGroup from 'components/ui/FormControlGroup';
import { ResourceSelect, SelectOption } from 'components/ui/ResourceCreatableSelect';
import SubmitButton from 'components/ui/SubmitButton';
import WrapperDialog from 'components/ui/WrapperDialog';
import { wrapperDialogDefaultProps } from 'features/emails/config';
import { useMessages } from 'hooks/useMessages';
import { useResourceList } from 'hooks/useResourceList';
import { FormProvider, Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useSaveNote } from '../hooks/useSaveNote';

export interface FormValues {
  title: string;
  subcategory:  SelectOption;
  contentGroupId: string;
  date?: Date;
  startsAt?: string;
  endsAt?: string;
  hideDate?: boolean;
}

interface FormNoteProps {
  contentGroup?: any;
  note?: any;
}

const validationSchema = Yup.object().shape({
  date: Yup.date().required(),
  title: Yup.string().required(),
  subcategory: Yup.object().required(),
  contentGroupId: Yup.string().required(),
  hideDate: Yup.boolean(),
});

export default function FormNote({ note, contentGroup }: FormNoteProps) {
  const isEditing = !!note;

  if (isEditing)
    contentGroup = note.contentGroup;

  const navigate = useNavigate();

  const defaultValues: FormValues = {
    date: note?.date ? new Date(note?.date) : undefined,
    title: note?.title,
    subcategory: note?.subcategory ? {
      value: note.subcategory.id,
      label: `${note.category.name} - ${note.subcategory.name}`
    }: null,
    contentGroupId: contentGroup.id,
    hideDate: note?.hideDate,
    startsAt: note?.startsAt,
    endsAt: note?.endsAt
  };

  const methods = useForm<FormValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema) as unknown as Resolver<FormValues>,
    defaultValues
  });

  const { register, setValue, control, handleSubmit, watch, reset, formState: { errors, isValid } } = methods;

  const selectedContentGroupId = watch('contentGroupId')

  const { onError, onSuccess } = useMessages({ success: 'Dados da nota atualizados com sucesso!' });

  const { data: contentGroups } = useResourceList({ resourceName: 'contentGroups', path: '/content-groups', params: { type: 'AGENDA' }});
  
  //const { data: categories, isLoading: isLoadingCategories } = useResourceList({ resourceName: 'categories', path: '/template-categories', params: { contentGroupId: selectedContentGroupId } });

  const { 
    onSaveNote, 
    isPending,
  } = useSaveNote({ 
        noteId: note?.id,
        onSuccess: (note) => {
          if (isEditing)
            onSuccess()
          else {
            reset();
            navigate(`/notas-de-agenda/nova/${note.id}/topicos`)
          }
          
        },
        onError: (error) => onError(error?.response?.data?.message)
      });
    
  const onSubmit: SubmitHandler<FormValues> = ({ subcategory, ...data }) => onSaveNote({
    ...data,
    subcategoryId: subcategory?.value as string
  });

  const { onChange: onChangeContentGroupId, ...contentGroupIdProps } = register('contentGroupId');
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardForm mt="30px">
          <FormControlGroup>
            <FormControl>
              <FormLabel>Modelo de disparo</FormLabel>
            { contentGroups && (
              <Select 
                {...contentGroupIdProps}
                onChange={(e) => {
                  setValue('subcategory', null, { shouldValidate: true })
                  onChangeContentGroupId(e);
                }}
                bgColor="green.100"
              >
              { contentGroups?.map(contentGroup => (
                <option key={contentGroup.id} value={contentGroup.id}>
                  {contentGroup?.name}
                </option>
              ))}
              </Select>
            )}
            </FormControl>
          
            <FormControl 
              isInvalid={!!errors.subcategory}
              isRequired
            >
              <FormLabel>Categoria</FormLabel>
              <ResourceSelect
                key={selectedContentGroupId}
                control={control}
                name="subcategory"
                resourceKey="categories"
                listPath="/template-categories/subcategories-list"
                listParams={{ contentGroupId: selectedContentGroupId }}
                placeholder="Selecione uma opção"
              />
            </FormControl>
          </FormControlGroup>

          <FormControlGroup zIndex={1}>
            <FormControl 
              isInvalid={!!errors.title} 
              isRequired
            >
              <FormLabel>Título</FormLabel>
              <Input {...register('title')} maxLength={160} />
            </FormControl>
            <FormControl 
              width="auto"
              isInvalid={!!errors.date}
              isRequired
            >
              <FormLabel>Data</FormLabel>
              <CustomSingleDatepicker
                date={defaultValues.date}
                onDateChange={(date) => setValue('date', date, { shouldValidate: true })}
              />
            </FormControl>
            <FormControl width="auto">
              <FormLabel whiteSpace="nowrap">Horário inícial</FormLabel>
              <Input type="time" {...register('startsAt')} />
            </FormControl>
            <FormControl width="auto">
              <FormLabel whiteSpace="nowrap">Horário final</FormLabel>
              <Input type="time" {...register('endsAt')} />
            </FormControl>
          </FormControlGroup>  

          <FormControl
            display="flex" 
            alignItems="center" 
            gap="10px"
            pt="22px"
            w="auto"
          >
            <Switch 
              id="hideDate" 
              {...register('hideDate')}
            />
            <FormLabel htmlFor="highlighted" mb="0" whiteSpace="nowrap">
              Ocultar data no e-mail
            </FormLabel>
          </FormControl>
        </CardForm>          

        <FixedBar>
        { isEditing && (
          <ButtonGroup ml="auto">
            <SubmitButton 
              isDisabled={!isValid}
              isLoading={isPending}
            >
              Atualizar
            </SubmitButton>
          </ButtonGroup>
        )}

        { !isEditing && (
          <>
            <WrapperDialog {...wrapperDialogDefaultProps}>
              <ButtonBack 
                as={Link}
                to="/notas-de-agenda"
              />
            </WrapperDialog>

            <ButtonGroup>
              {/* <SaveButton 
                isDisabled={!isValid}
                isLoading={variables?.onlySave && isPending}
                onClick={handleSubmit(onSave)}
              >
                Salvar
              </SaveButton> */}

              <SubmitButton 
                isDisabled={!isValid}
                //isLoading={!variables?.onlySave && isPending}
                isLoading={isPending}
              >
                Salvar e continuar
              </SubmitButton>
            </ButtonGroup>
          </>
        )}

        </FixedBar>
      </form>
    </FormProvider>
  )
}
